import BOOK_COVERS from "./bookCovers.json";

// Backend base URL (ISF-API on DigitalOcean)
const API_BASE = "https://dolphin-app-33jp4.ondigitalocean.app";

// SkillMaster module routes (login, signup, profile, admin config, books)
export const SKILLMASTER_API = `${API_BASE}/skill_master`;

// Same router is also mounted at /skillmaster on some deployments
const ADMIN_CONFIG_URLS = [
  `${API_BASE}/skill_master/admin/config`,
  `${API_BASE}/skillmaster/admin/config`,
];

const CATEGORIES = ["K12", "Mathematics", "Vocational", "Industry", "Technology", "Agriculture", "UPSC", "Medical"];

// Mirrors the backend default: everything visible, nothing free
export const DEFAULT_ADMIN_CONFIG = {
  courseVisibility: Object.fromEntries(CATEGORIES.map((c) => [c, true])),
  freeBookSettings: Object.fromEntries(
    CATEGORIES.map((c) => [c, { enabled: false, allFree: false, freeBooks: [] }])
  ),
};

/**
 * Load admin config. Tries both mounts; if neither returns a valid config,
 * falls back to DEFAULT_ADMIN_CONFIG so books are never hidden because of a
 * config/DB error.
 */
export const fetchAdminConfig = async () => {
  for (const url of ADMIN_CONFIG_URLS) {
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const data = await response.json();
      if (data.success && data.config && data.config.courseVisibility) {
        return data.config;
      }
      console.warn(`Admin config not usable from ${url}:`, data.message);
    } catch (err) {
      console.warn(`Admin config request failed for ${url}:`, err);
    }
  }
  console.warn("Using default admin config (all categories visible)");
  return DEFAULT_ADMIN_CONFIG;
};

// Local placeholder (via.placeholder.com was shut down): book title on a plain
// background, wrapped onto up to 3 lines. Returns an SVG data URI.
export const placeholderImage = (text = "Book", width = 300, height = 400) => {
  const maxChars = Math.max(8, Math.floor(width / 18));
  // split on spaces and "/", then hard-break any word still longer than a line
  const words = String(text || "Book")
    .replace(/[<>"']/g, "")
    .replace(/\//g, "/ ")
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((w) => w.match(new RegExp(`.{1,${maxChars}}`, "g")) || []);
  const lines = [];
  let line = "";
  words.forEach((w) => {
    if ((line + " " + w).trim().length > maxChars && line) {
      lines.push(line);
      line = w;
    } else {
      line = (line + " " + w).trim();
    }
  });
  if (line) lines.push(line);
  const shown = lines.slice(0, 3);
  if (lines.length > 3) shown[2] = shown[2].slice(0, maxChars - 1) + "…";

  const fontSize = Math.round(Math.min(width, height) / 12);
  const lineH = fontSize * 1.25;
  const startY = height / 2 - ((shown.length - 1) * lineH) / 2;
  const tspans = shown
    .map((l, i) => `<tspan x="50%" y="${startY + i * lineH}">${l.replace(/&/g, "&amp;")}</tspan>`)
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="100%" height="100%" fill="#E2E8F0"/><text fill="#334155" font-family="Poppins, Arial, sans-serif" font-size="${fontSize}" font-weight="600" text-anchor="middle" dominant-baseline="middle">${tspans}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// onError handler: swap a broken image for the placeholder (only once, no loop)
export const imageFallback = (text, width, height) => (e) => {
  const img = e.currentTarget;
  if (img.dataset.fallback) return;
  img.dataset.fallback = "1";
  img.src = placeholderImage(text, width, height);
};

// Background style that shows the title cover while the real image is still loading
// (a slow/hanging image server would otherwise leave an empty grey box)
export const placeholderBackground = (text, width, height) => ({
  backgroundImage: `url("${placeholderImage(text, width, height)}")`,
  backgroundSize: "cover",
  backgroundPosition: "center",
});

// onLoad handler: once the real image is in, drop the placeholder background behind it
export const clearPlaceholderBackground = (e) => {
  const parent = e.currentTarget.parentElement;
  if (parent) parent.style.backgroundImage = "none";
};

// ---------------- Book covers ----------------
// bookCovers.json maps "<source>|<id>" -> cover hosted on skillmaster.net.in.
// Used only when the book's own image is missing, dead (old S3) or fails to load.
const coverFor = (book) => (book ? BOOK_COVERS[`${book.source}|${book.id}`] : undefined);
const isDeadImage = (url) => !url || !/^https?:\/\//i.test(url) || /amazonaws\.com/i.test(url);

// src for a book image: own image -> our cover -> title placeholder
export const bookImageSrc = (book, width, height) =>
  !isDeadImage(book?.pdf_img)
    ? book.pdf_img
    : coverFor(book) || placeholderImage(book?.pdf_name, width, height);

// onError: try our cover once, then the title placeholder (no loops)
export const bookImageFallback = (book, width, height) => (e) => {
  const img = e.currentTarget;
  const cover = coverFor(book);
  if (cover && !img.dataset.coverTried && !img.src.endsWith(cover)) {
    img.dataset.coverTried = "1";
    img.src = cover;
    return;
  }
  if (img.dataset.fallback) return;
  img.dataset.fallback = "1";
  img.src = placeholderImage(book?.pdf_name, width, height);
};

// Background shown while the image loads (a slow image server would otherwise show an empty box)
export const bookImageBackground = (book, width, height) => {
  const cover = coverFor(book);
  return {
    backgroundImage: `url("${cover || placeholderImage(book?.pdf_name, width, height)}")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };
};

export default API_BASE;
